'use client';

/**
 * Main support chat widget component
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import type {
  SupportChatConfig,
  SupportChatUser,
  SupportChatOfflineFallbackOptions,
  SupportChatRatingOptions,
} from '../types/support-chat';
import { useSupportChat } from '../hooks/useSupportChat';
import { ChatHeader } from './ChatHeader';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { ThreadList } from './ThreadList';
import { UserInfoForm, type UserInfoFormData } from './UserInfoForm';
import { SettingsModal } from './SettingsModal';
import { InfoModal } from './InfoModal';
import { getStoredUserInfo, storeUserInfo, clearStoredUserInfo } from '../lib/user-storage';
import type { ThreadId } from '../types/support-chat';

interface SupportChatWidgetProps {
  config: SupportChatConfig;
  user: SupportChatUser;
  /** Custom title for the chat widget */
  title?: string;
  /** Custom placeholder for input */
  inputPlaceholder?: string;
  /** Controlled mode: whether the widget is open. When provided with onOpenChange, the widget is controlled. */
  open?: boolean;
  /** Controlled mode: callback when the widget should close. When provided with open, the widget is controlled. */
  onOpenChange?: (open: boolean) => void;
  /** Optional bearer token for API authentication. When provided, will be sent in Authorization header. */
  bearerToken?: string;
  /**
   * OPT-IN: enable file attachments in the input. Default false. Only the host
   * app that passes this gets file upload (the package is shared by ~22 apps).
   */
  enableFileUpload?: boolean;
  /**
   * OPT-IN: offline fallback. When the customer waits past the timeout with no
   * agent reply, show a "turn this into a ticket" button. Undefined = disabled.
   */
  offlineFallback?: SupportChatOfflineFallbackOptions;
  /**
   * OPT-IN: post-chat 👍/👎 satisfaction rating. Undefined = disabled.
   */
  rating?: SupportChatRatingOptions;
}

const DEFAULT_REPLY_TIMEOUT_MS = 120000; // 2 minutes

export function SupportChatWidget({
  config,
  user: initialUser,
  title = 'Support Chat',
  inputPlaceholder = 'Type your message...',
  open: controlledOpen,
  onOpenChange,
  bearerToken,
  enableFileUpload = false,
  offlineFallback,
  rating,
}: SupportChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false);
  const [viewMode, setViewMode] = useState<'thread' | 'list'>('thread');
  // TYPING_FOCUS_STABILITY: activity timestamp is a ref, NOT state — a keystroke
  // handler that bumps state re-renders the whole widget on every character.
  // Only `isUserActive` (which actually changes polling behavior) is state, and
  // setState(true) while already true is a no-op, so active typing causes zero
  // widget re-renders from activity tracking.
  const lastActivityRef = useRef<number>(Date.now());
  const [isUserActive, setIsUserActive] = useState<boolean>(true);

  // Determine if we're in controlled mode
  const isControlled = controlledOpen !== undefined && onOpenChange !== undefined;
  // Use controlled open if provided, otherwise use internal state
  const effectiveOpen = isControlled ? controlledOpen : isOpen;

  // State for user info collection
  const [needsUserInfo, setNeedsUserInfo] = useState(false);
  const [user, setUser] = useState<SupportChatUser>(initialUser);
  const [isCollectingUserInfo, setIsCollectingUserInfo] = useState(false);
  const [userInfoError, setUserInfoError] = useState<string | null>(null);
  const [hasCheckedUserInfo, setHasCheckedUserInfo] = useState(false);

  // Track which message ID is currently streaming
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);

  const isAuthenticated = user.isAuthenticated !== false && user.accessToken !== undefined && user.cognitoSub !== undefined;

  // Settings modal state
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  
  // Info modal state
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const {
    messages,
    threadId,
    threads,
    sendMessage,
    isLoading,
    error,
    switchThread,
    createOrGetThread,
    createThreadWithContext,
    updateThreads,
  } = useSupportChat(config, user, effectiveOpen, isUserActive, bearerToken);

  // Check if user info is needed when chat opens
  useEffect(() => {
    if (!effectiveOpen || hasCheckedUserInfo) {
      return;
    }

    setHasCheckedUserInfo(true);

    if (!isAuthenticated && config.cookieStorage) {
      // Check if we have stored user info
      const storedInfo = getStoredUserInfo(config.cookieStorage.cookieName);
      
      if (storedInfo) {
        // Update user with stored info
        setUser((prev) => ({
          ...prev,
          email: storedInfo.email,
          name: storedInfo.name,
        }));
        setNeedsUserInfo(false);

        // If no existing threads, create one with browser context
        if (threads.length === 0 && !threadId) {
          createThreadWithContext({
            userName: storedInfo.name, // Include name since we have it stored
            includeBrowserContext: true,
          }).catch((err) => {
            console.error('Failed to create thread:', err);
          });
        }
      } else {
        // Need to collect user info
        setNeedsUserInfo(true);
      }
    } else if (isAuthenticated && threads.length === 0 && !threadId) {
      // Authenticated user with no threads - create thread with browser context
      createThreadWithContext({
        includeBrowserContext: true,
      }).catch((err) => {
        console.error('Failed to create thread:', err);
      });
    }
  }, [effectiveOpen, isAuthenticated, config.cookieStorage, threads.length, threadId, hasCheckedUserInfo, createThreadWithContext]);

  // Track streaming messages: when a new message arrives from support, mark it as streaming
  useEffect(() => {
    if (messages.length === 0) {
      setStreamingMessageId(null);
      return;
    }

    // Get the last message
    const lastMessage = messages[messages.length - 1];

    // If it's a support message and not from the user, mark it as streaming
    if (lastMessage.sender.type === 'support' && !lastMessage.isUser) {
      setStreamingMessageId(lastMessage.id);

      // Stop streaming after a delay (message fully displayed)
      const timer = setTimeout(() => {
        setStreamingMessageId(null);
      }, 3000); // Stop streaming after 3 seconds of the message being added

      return () => clearTimeout(timer);
    } else {
      setStreamingMessageId(null);
    }
  }, [messages]);

  // Reset check flag when chat closes
  useEffect(() => {
    if (!effectiveOpen) {
      setHasCheckedUserInfo(false);
    }
  }, [effectiveOpen]);

  // Handle user info form submission
  const handleUserInfoSubmit = useCallback(
    async (data: UserInfoFormData) => {
      setIsCollectingUserInfo(true);
      setUserInfoError(null);

      try {
        // Store user info in localStorage
        if (config.cookieStorage) {
          storeUserInfo(data.name, data.email, config.cookieStorage.cookieName);
        }

        // Update user object with real email and name
        const updatedUser: SupportChatUser = {
          ...user,
          email: data.email,
          name: data.name,
        };
        setUser(updatedUser);

        // Create thread with user info and browser context
        // Pass the updated user data directly to ensure the real email is used
        const threadInfo = await createThreadWithContext({
          userName: data.name,
          userEmail: data.email, // Pass real email explicitly
          includeBrowserContext: true,
        });

        if (threadInfo) {
          // Success - hide form and show chat
          setNeedsUserInfo(false);
          setIsCollectingUserInfo(false);
        } else {
          throw new Error('Failed to create thread');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to start chat';
        setUserInfoError(errorMessage);
        setIsCollectingUserInfo(false);
      }
    },
    [user, config.cookieStorage, createThreadWithContext]
  );

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    // Reset activity when opening chat
    lastActivityRef.current = Date.now();
    setIsUserActive(true);
    // Trigger auto-focus after a short delay to ensure the input is rendered
    setShouldAutoFocus(true);
    setTimeout(() => setShouldAutoFocus(false), 200);
  }, []);

  const handleClose = useCallback(() => {
    if (isControlled && onOpenChange) {
      onOpenChange(false);
    } else {
      setIsOpen(false);
    }
  }, [isControlled, onOpenChange]);

  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIsUserActive(true);
  }, []);

  // Check for inactivity (1 minute = 60000ms)
  React.useEffect(() => {
    if (!effectiveOpen || viewMode !== 'thread') {
      return;
    }

    const checkInactivity = () => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      const oneMinute = 60000;

      if (timeSinceLastActivity >= oneMinute && isUserActive) {
        setIsUserActive(false);
      } else if (timeSinceLastActivity < oneMinute && !isUserActive) {
        setIsUserActive(true);
      }
    };

    const interval = setInterval(checkInactivity, 1000); // Check every second

    return () => clearInterval(interval);
  }, [effectiveOpen, viewMode, isUserActive]);

  const handleSend = useCallback(
    async (message: string, files?: File[]) => {
      handleActivity(); // Mark activity when sending
      await sendMessage(message, files);
    },
    [sendMessage, handleActivity]
  );

  const handleSelectThread = useCallback(
    (selectedThreadId: ThreadId) => {
      switchThread(selectedThreadId);
      setViewMode('thread');
      // Reset activity when switching threads
      lastActivityRef.current = Date.now();
      setIsUserActive(true);
      setShouldAutoFocus(true);
      setTimeout(() => setShouldAutoFocus(false), 200);
    },
    [switchThread]
  );

  const handleCreateNewThread = useCallback(async () => {
    const info = await createThreadWithContext({
      userName: user.name,
      includeBrowserContext: true,
    });
    if (info) {
      switchThread(info.threadId);
      setViewMode('thread');
      setShouldAutoFocus(true);
      setTimeout(() => setShouldAutoFocus(false), 200);
    }
  }, [createThreadWithContext, user, switchThread]);

  const handleBackToList = useCallback(() => {
    setViewMode('list');
  }, []);

  const handleOpenSettings = useCallback(() => {
    setIsSettingsOpen(true);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  const handleOpenInfo = useCallback(() => {
    setIsInfoOpen(true);
  }, []);

  const handleCloseInfo = useCallback(() => {
    setIsInfoOpen(false);
  }, []);

  const handleClearThreadHistory = useCallback(async (): Promise<boolean> => {
    try {
      // Clear all threads by passing empty array
      const success = await updateThreads([]);
      if (success) {
        // After clearing, threads array will be empty, so threadId should naturally become invalid
        // Just ensure we're in list view (which will be empty now)
        setViewMode('list');
      }
      return success;
    } catch (err) {
      console.error('Failed to clear thread history:', err);
      return false;
    }
  }, [updateThreads]);

  const handleClearUserInfo = useCallback(() => {
    if (config.cookieStorage) {
      clearStoredUserInfo(config.cookieStorage.cookieName);
      // Reset user to initial state
      setUser(initialUser);
      setNeedsUserInfo(true);
    }
  }, [config.cookieStorage, initialUser]);

  // When chat opens, show list view if there are threads, otherwise show thread view
  React.useEffect(() => {
    if (effectiveOpen && threads.length > 0 && !threadId) {
      setViewMode('list');
    } else if (effectiveOpen && threadId) {
      setViewMode('thread');
    }
  }, [effectiveOpen, threads.length, threadId]);

  const [showTooltip, setShowTooltip] = useState(false);

  // ---- OPT-IN: offline fallback + post-chat rating (Hub-only via props) ----
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  const [isConverting, setIsConverting] = useState(false);
  const [convertResult, setConvertResult] = useState<
    { ok: boolean; message?: string } | null
  >(null);
  const [ratingValue, setRatingValue] = useState<'up' | 'down' | null>(null);
  const [isRating, setIsRating] = useState(false);

  // Tick a clock only while the offline-fallback feature is enabled and the
  // chat is open, so we can measure "time since the customer's last message".
  useEffect(() => {
    if (!offlineFallback || !effectiveOpen || viewMode !== 'thread') {
      return;
    }
    const interval = setInterval(() => setNowTick(Date.now()), 5000);
    return () => clearInterval(interval);
  }, [offlineFallback, effectiveOpen, viewMode]);

  // Reset feature state when the thread changes or the chat closes.
  useEffect(() => {
    setConvertResult(null);
    setRatingValue(null);
    setIsConverting(false);
    setIsRating(false);
  }, [threadId, effectiveOpen]);

  const lastUserMessage = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].isUser) return messages[i];
    }
    return null;
  })();
  // A genuine human agent reply is sender.type === 'support'. The bot-posted
  // agent toolbar is 'system' (it has a bot_id), so it does NOT count as a
  // reply and won't suppress the fallback.
  const hasAgentReplyAfterLastUser =
    !!lastUserMessage &&
    messages.some(
      (m) =>
        m.sender.type === 'support' &&
        new Date(m.timestamp).getTime() >
          new Date(lastUserMessage.timestamp).getTime()
    );
  const hasAnyAgentReply = messages.some((m) => m.sender.type === 'support');
  const replyTimeoutMs = offlineFallback?.replyTimeoutMs ?? DEFAULT_REPLY_TIMEOUT_MS;
  const waitedMs = lastUserMessage
    ? nowTick - new Date(lastUserMessage.timestamp).getTime()
    : 0;
  const showOfflineFallback =
    !!offlineFallback &&
    !!threadId &&
    !!lastUserMessage &&
    !hasAgentReplyAfterLastUser &&
    waitedMs >= replyTimeoutMs &&
    !convertResult?.ok;
  const showRatingPrompt =
    !!rating &&
    !!threadId &&
    (hasAnyAgentReply || convertResult?.ok === true) &&
    ratingValue === null;

  const buildTranscript = useCallback((): string => {
    return messages
      .filter((m) => m.text && m.text.trim().length > 0)
      .map((m) => {
        const who = m.isUser
          ? user.name || 'Customer'
          : m.sender.type === 'system'
          ? 'System'
          : m.sender.name || 'Agent';
        return `${who}: ${m.text}`;
      })
      .join('\n');
  }, [messages, user.name]);

  const handleConvertToTicket = useCallback(async () => {
    if (!offlineFallback || !threadId || isConverting) return;
    setIsConverting(true);
    try {
      const result = await offlineFallback.onConvertToTicket({
        threadId,
        userEmail: user.email,
        userName: user.name,
        transcript: buildTranscript(),
      });
      setConvertResult(result);
    } catch {
      setConvertResult({
        ok: false,
        message:
          "We couldn't create a ticket just now. Please call (901) 613-1510 or email info@westtn.consulting.",
      });
    } finally {
      setIsConverting(false);
    }
  }, [offlineFallback, threadId, isConverting, user.email, user.name, buildTranscript]);

  const handleRate = useCallback(
    async (value: 'up' | 'down') => {
      if (!rating || !threadId || isRating || ratingValue) return;
      setRatingValue(value); // optimistic
      setIsRating(true);
      try {
        await rating.onRate(value, { threadId, userEmail: user.email });
      } catch {
        // Keep the optimistic value; a failed rating write isn't worth nagging.
      } finally {
        setIsRating(false);
      }
    },
    [rating, threadId, isRating, ratingValue, user.email]
  );

  // When controlled and closed, render nothing
  if (isControlled && !effectiveOpen) {
    return null;
  }

  if (!effectiveOpen) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 9999,
        }}
      >
        <button
          onClick={handleOpen}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: showTooltip ? '#2563eb' : '#3b82f6',
            color: '#ffffff',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            fontSize: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            transition: 'background-color 0.2s, transform 0.2s',
            transform: showTooltip ? 'scale(1.05)' : 'scale(1)',
          }}
          aria-label="Open support chat"
        >
          💬
          {showTooltip && (
            <span
              style={{
                position: 'absolute',
                bottom: '100%',
                right: '0',
                marginBottom: '8px',
                padding: '6px 12px',
                backgroundColor: '#1f2937',
                color: '#ffffff',
                fontSize: '12px',
                fontWeight: 500,
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                pointerEvents: 'none',
                zIndex: 10000,
              }}
            >
              {title}
              <span
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: '12px',
                  width: 0,
                  height: 0,
                  borderLeft: '4px solid transparent',
                  borderRight: '4px solid transparent',
                  borderTop: '4px solid #1f2937',
                }}
              />
            </span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        width: '380px',
        maxWidth: 'calc(100vw - 40px)',
        height: '600px',
        maxHeight: 'calc(100vh - 40px)',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      <ChatHeader
        title={title}
        onClose={handleClose}
        onBack={viewMode === 'thread' ? handleBackToList : undefined}
        showBackButton={viewMode === 'thread' && threads.length > 0}
        onSettings={handleOpenSettings}
        onInfo={handleOpenInfo}
      />

      {needsUserInfo ? (
        <div
          style={{
            flex: 1,
            overflow: 'hidden',
            opacity: 1,
            transition: 'opacity 0.3s ease-in',
          }}
        >
          <UserInfoForm
            onSubmit={handleUserInfoSubmit}
            isLoading={isCollectingUserInfo}
            error={userInfoError}
          />
        </div>
      ) : viewMode === 'list' ? (
        <ThreadList
          threads={threads}
          currentThreadId={threadId}
          onSelectThread={handleSelectThread}
          onCreateNewThread={handleCreateNewThread}
          isLoading={isLoading}
        />
      ) : (
        <>
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              backgroundColor: '#ffffff',
            }}
          >
            {isLoading && messages.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '14px',
                  padding: '20px',
                }}
              >
                Loading...
              </div>
            )}

            {error && messages.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  color: '#ef4444',
                  fontSize: '14px',
                  padding: '20px',
                }}
              >
                Error: {error}
              </div>
            )}

            {messages.length === 0 && !isLoading && !error && (
              <div
                style={{
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '14px',
                  padding: '20px',
                }}
              >
                Start a conversation with our support team...
              </div>
            )}

            {messages.map((message) => (
              <ChatMessage 
                key={message.id} 
                message={message}
                isStreaming={streamingMessageId === message.id}
              />
            ))}
          </div>

          {/* OPT-IN: offline fallback — convert this chat into a ticket */}
          {(showOfflineFallback || convertResult) && (
            <div
              style={{
                borderTop: '1px solid #e5e7eb',
                backgroundColor: convertResult?.ok ? '#f0fdf4' : '#fffbeb',
                padding: '12px 16px',
              }}
            >
              {convertResult ? (
                <div
                  style={{
                    fontSize: '13px',
                    color: convertResult.ok ? '#166534' : '#92400e',
                    lineHeight: 1.5,
                  }}
                >
                  {convertResult.message ||
                    (convertResult.ok
                      ? "We've logged your request as a ticket and emailed you a confirmation."
                      : "We couldn't create a ticket just now. Please call (901) 613-1510.")}
                </div>
              ) : (
                <>
                  <div
                    style={{
                      fontSize: '13px',
                      color: '#92400e',
                      marginBottom: '8px',
                      lineHeight: 1.5,
                    }}
                  >
                    Haven&apos;t heard back yet? Turn this conversation into a support
                    ticket and we&apos;ll follow up by email — nothing gets lost.
                  </div>
                  <button
                    onClick={handleConvertToTicket}
                    disabled={isConverting}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: isConverting ? '#d1d5db' : '#3b82f6',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isConverting ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      fontWeight: 600,
                    }}
                  >
                    {isConverting ? 'Creating ticket…' : 'Turn this into a ticket'}
                  </button>
                </>
              )}
            </div>
          )}

          {/* OPT-IN: post-chat satisfaction rating */}
          {(showRatingPrompt || ratingValue) && (
            <div
              style={{
                borderTop: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb',
                padding: '10px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              {ratingValue ? (
                <span style={{ fontSize: '13px', color: '#6b7280' }}>
                  Thanks for your feedback!
                </span>
              ) : (
                <>
                  <span style={{ fontSize: '13px', color: '#374151' }}>
                    Was this helpful?
                  </span>
                  <button
                    onClick={() => handleRate('up')}
                    disabled={isRating}
                    aria-label="Yes, helpful"
                    style={{
                      padding: '4px 8px',
                      backgroundColor: 'transparent',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: isRating ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    👍
                  </button>
                  <button
                    onClick={() => handleRate('down')}
                    disabled={isRating}
                    aria-label="No, not helpful"
                    style={{
                      padding: '4px 8px',
                      backgroundColor: 'transparent',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: isRating ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    👎
                  </button>
                </>
              )}
            </div>
          )}

          <ChatInput
            onSend={handleSend}
            // TYPING_FOCUS_STABILITY: never gate the composer on background
            // load state. `isLoading` toggles on every poll; disabling a
            // focused textarea blurs the user mid-type. ChatInput self-guards
            // an in-flight send with its own internal `isSending`.
            placeholder={inputPlaceholder}
            autoFocus={shouldAutoFocus}
            onActivity={handleActivity}
            enableFileUpload={enableFileUpload}
          />
        </>
      )}

      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={handleCloseSettings}
        config={config}
        user={user}
        onClearThreadHistory={handleClearThreadHistory}
        onClearUserInfo={!isAuthenticated ? handleClearUserInfo : undefined}
      />

      <InfoModal
        isOpen={isInfoOpen}
        onClose={handleCloseInfo}
      />
    </div>
  );
}

